// src/components/ParentHubView.jsx
//
// The Parent Hub: what a parent on a youth-sport org sees. It is about their
// player, not about them. Three things, in order of what a parent came for:
//   1. The monthly check-in about their player (ASSESSMENT_LIBRARY parent_v1)
//      and what it says: a short list of what is worth a conversation, with
//      the talk guide that fits each item. No score, no chart.
//   2. The talk guides themselves (data/talkGuides.js), as a library.
//   3. Someone to talk to: the org's support outlet, which for a parent carries
//      the caregiver escalation routes (data/supportPathways.js).
//
// Privacy: a parent's answers are their own record, read from the same
// self-only private metrics as anyone else's. They are never matched to the
// player's account, never shown to a coach as this parent's, and never shown to
// the player. The hub says so.
//
// Routed from resolveSurface() === 'parent': the org's Parent member-type slot
// set to the 'parent' surface (OrganizationManager -> Member types).

import React, { useState } from 'react';
import { ClipboardCheck, MessageCircle, ChevronDown, ChevronRight, Users, BookOpen, HeartHandshake, CheckCircle2, Eye } from 'lucide-react';
import { useOrganization } from '../context/OrganizationContext';
import { heroClass, heroStyle, heroEyebrowStyle } from '../utils/orgTheme';
import { talkGuidesFor, parentRead } from '../data/talkGuides';
import { resolveSport } from '../data/sports';
import SupportOutlet from './dashboard/SupportOutlet';
import { recordContentEvent } from '../utils/contentEvents';

const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
};

const GuideCard = ({ guide, open, onToggle, context }) => (
    <div className={`bg-white rounded-2xl border transition-colors ${open ? 'border-brand-primary/40 shadow-sm' : 'border-slate-200'}`}>
        <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-3 p-4 text-left">
            <div className="min-w-0">
                {context && <p className="text-[10px] font-black uppercase tracking-widest text-brand-primary mb-0.5">{context}</p>}
                <p className="font-black text-slate-900 leading-tight">{guide.title}</p>
                {!open && <p className="text-xs text-slate-500 font-medium mt-0.5 truncate">{guide.when}</p>}
            </div>
            <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
            <div className="px-4 pb-5 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <p className="text-sm text-slate-600 font-medium leading-relaxed">{guide.when}</p>
                <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2">Say first</p>
                    <ul className="space-y-1.5">{guide.first.map((s, i) => <li key={i} className="text-sm text-slate-800 font-medium leading-snug flex gap-2"><span className="text-brand-primary shrink-0">•</span><span>{s}</span></li>)}</ul>
                </div>
                <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2">Leave out</p>
                    <ul className="space-y-1.5">{guide.avoid.map((s, i) => <li key={i} className="text-sm text-slate-600 font-medium leading-snug flex gap-2"><span className="text-slate-300 shrink-0">•</span><span>{s}</span></li>)}</ul>
                </div>
                <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--brand-highlight, #F8FAFC)' }}>
                    <p className="text-[11px] font-black uppercase tracking-widest text-brand-primary mb-1">The one question</p>
                    <p className="text-sm font-black text-slate-900">“{guide.ask}”</p>
                </div>
                <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-1">When to get more help</p>
                    <p className="text-sm text-slate-600 font-medium leading-relaxed">{guide.more}</p>
                </div>
            </div>
        )}
    </div>
);

const ParentHubView = ({ currentUser, onNavigate }) => {
    const { orgData, isPreviewMode } = useOrganization();
    const [openGuide, setOpenGuide] = useState(null);
    const sport = resolveSport(orgData);
    const guides = talkGuidesFor(sport);
    const read = parentRead(currentUser?.latestStats, sport);
    const hasRead = read.answered > 0;
    const last = formatDate(currentUser?.lastAssessmentDate);
    const playerWord = `your ${sport.person || 'player'}`;
    const toggle = (id) => setOpenGuide(curr => {
        const next = curr === id ? null : id;
        if (next) recordContentEvent('guide_open', { user: currentUser, org: orgData, isPreviewMode, topicId: 'talk-guides', resourceId: String(id).replace(/^watch:/, ''), resourceType: 'Guide', surface: 'parent' });
        return next;
    });

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in pb-24">
            {/* HERO */}
            <div className={`${heroClass(orgData)} rounded-3xl p-6 md:p-8 text-white relative overflow-hidden`} style={heroStyle(orgData)}>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 text-white/70 text-[11px] font-black uppercase tracking-widest mb-2" style={heroEyebrowStyle(orgData)}>
                            <Users size={15} /> Parent Hub
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">How is {playerWord} doing?</h1>
                        <p className="text-white/80 font-medium mt-2 max-w-xl leading-relaxed">
                            A two-minute check-in about {playerWord}, once a month. It doesn't score them. It tells you what might be worth a conversation, and gives you the words.
                        </p>
                    </div>
                    <div className="shrink-0 flex flex-col items-start md:items-end gap-2">
                        <button
                            type="button"
                            onClick={() => onNavigate && onNavigate('assessment')}
                            className="px-6 py-3 bg-white text-brand-primary rounded-full font-black text-sm flex items-center gap-2 hover:bg-white/90 transition-colors shadow-lg"
                        >
                            <ClipboardCheck size={18} /> {hasRead ? 'Update this month\'s check-in' : 'Start the check-in'}
                        </button>
                        {last && <p className="text-xs text-white/70 font-medium">Last check-in: {last}</p>}
                    </div>
                </div>
            </div>

            {/* WHAT THE CHECK-IN SAYS */}
            {hasRead ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                        <div>
                            <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-3"><MessageCircle size={16} /> Worth a conversation</h2>
                            {read.watch.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-3">
                                    <CheckCircle2 size={20} className="text-emerald-500 shrink-0 mt-0.5" />
                                    <p className="text-sm text-slate-700 font-medium leading-relaxed">Nothing you rated low this month. The guides below are still there for whenever something comes up.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {read.watch.map(w => (
                                        <GuideCard
                                            key={w.key}
                                            guide={w.guide}
                                            context={w.label}
                                            open={openGuide === `watch:${w.key}`}
                                            onToggle={() => toggle(`watch:${w.key}`)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="space-y-4">
                        {read.steady.length > 0 && (
                            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-3"><Eye size={14} /> Keep an eye on</h3>
                                <ul className="space-y-1.5">{read.steady.map(s => <li key={s.key} className="text-sm text-slate-700 font-medium">{s.label}</li>)}</ul>
                            </div>
                        )}
                        {read.good.length > 0 && (
                            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-3"><CheckCircle2 size={14} className="text-emerald-500" /> Going well</h3>
                                <ul className="space-y-1.5">{read.good.map(s => <li key={s.key} className="text-sm text-slate-700 font-medium">{s.label}</li>)}</ul>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8">
                    <p className="text-lg font-black text-slate-900">Start with the check-in.</p>
                    <p className="text-sm text-slate-600 font-medium leading-relaxed mt-1 max-w-2xl">Thirteen quick sliders about {playerWord}: sleep, energy, enjoying {sport.sport}, bouncing back, feeling part of {sport.id === 'school' ? 'their class' : 'the team'}, talking to you when something's wrong. Then this page shows what might be worth a conversation and gives you the words for it.</p>
                    <button type="button" onClick={() => onNavigate && onNavigate('assessment')} className="mt-4 px-5 py-3 bg-brand-primary hover:bg-brand-dark text-white rounded-full font-black text-sm flex items-center gap-2 transition-colors">
                        Start the check-in <ChevronRight size={16} />
                    </button>
                </div>
            )}

            {/* THE GUIDES */}
            <div>
                <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-3"><BookOpen size={16} /> Talk guides</h2>
                <p className="text-sm text-slate-500 font-medium mb-4">One card per situation. What to say first, what to leave out, the one question worth asking, and when to get more help.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {guides.map(g => (
                        <GuideCard key={g.id} guide={g} open={openGuide === g.id} onToggle={() => toggle(g.id)} />
                    ))}
                </div>
            </div>

            {/* SOMEONE TO TALK TO */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-brand-light text-brand-primary flex items-center justify-center shrink-0"><HeartHandshake size={20} /></div>
                    <div>
                        <p className="font-black text-slate-900">When it's more than a conversation</p>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed mt-0.5">Where a concern goes next, and the lines that are open now.</p>
                    </div>
                </div>
                <SupportOutlet currentUser={currentUser} className="px-5 py-3 bg-brand-primary hover:bg-brand-dark text-white rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-colors shrink-0" />
            </div>

            <p className="text-[11px] text-slate-400 font-medium leading-relaxed text-center px-4">
                Your answers are yours. They are never shown to {playerWord}, never matched to their account, and never shown to a coach as yours.
            </p>
        </div>
    );
};

export default ParentHubView;
